/**
 * Parses and validates a resource ID string, converting it to a number.
 *
 * The backend (Kotlin/Spring Boot) expects resource IDs as Long (number),
 * but Ember Data models always have string IDs. This utility ensures strict
 * validation before conversion.
 *
 * @param resourceIdString - The resource ID as a string (e.g., from model.id)
 * @returns The resource ID as a number
 * @throws {Error} If the string is not a pure numeric string (only digits)
 *
 * @example
 * parseResourceId('12345')  // returns 12345
 * parseResourceId('0')      // returns 0
 * parseResourceId('00789')  // returns 789 (leading zeros stripped)
 * parseResourceId('123abc') // throws Error
 * parseResourceId('')       // throws Error
 */
export function parseResourceId(resourceIdString: string): number {
  // Validate that the ID is a pure numeric string (only digits)
  // This prevents parseInt from partially parsing strings like "123abc" as 123
  if (!/^\d+$/.test(resourceIdString)) {
    throw new Error(`Invalid resource ID: cannot convert "${resourceIdString}" to number`);
  }

  return parseInt(resourceIdString, 10);
}
